import type { PlantingLog } from '@/lib/plantingTracker';
import type { WeatherData } from '@/lib/weather';
import type { ResolvedPlant } from '@/lib/plantResolver';
import type { AutoTaskState } from '@/lib/dataSync';
import { todayLocalISO } from '@/lib/localDate';

// Guidance guardrail: active care surfaces should build this context instead of
// hand-rolling planned, future-planted, or active-planted filtering.
export interface GuidanceTaskLike {
  category?: string;
  dueDate: string;
  dueDateOverride?: string;
}

export interface GuidanceContextInput {
  logs?: PlantingLog[];
  plannedPlants?: string[];
  weather?: WeatherData | null;
  today?: string;
  zone?: number | null;
  plantLookup?: Map<string, ResolvedPlant>;
  taskState?: AutoTaskState | null;
}

export interface GuidanceContext {
  today: string;
  anchorDate: string;
  zone: number | null;
  weather: WeatherData | null;
  plantLookup: Map<string, ResolvedPlant>;
  resolvedPlants: Map<string, ResolvedPlant>;
  allPlantingLogs: PlantingLog[];
  activePlantedLogs: PlantingLog[];
  futurePlantedLogs: PlantingLog[];
  plannedOnlyPlantIds: string[];
  taskState?: AutoTaskState | null;
}

export function guidanceAnchorDate(weather?: WeatherData | null, today?: string): string {
  return today ?? weather?.today?.date ?? todayLocalISO();
}

export function isoDateAtUtcNoon(isoDate: string): Date {
  return new Date(`${isoDate}T12:00:00Z`);
}

export function daysBetweenIsoDates(fromIso: string, toIso: string): number {
  const from = isoDateAtUtcNoon(fromIso).getTime();
  const to = isoDateAtUtcNoon(toIso).getTime();
  return Math.floor((to - from) / 86_400_000);
}

export function hasValidPlantedDate(log: Pick<PlantingLog, 'plantedDate'>): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(log.plantedDate) &&
    !Number.isNaN(isoDateAtUtcNoon(log.plantedDate).getTime());
}

export function isActivePlantedLog(log: PlantingLog, today: string): boolean {
  return log.status !== 'finished' &&
    log.status !== 'dormant' &&
    hasValidPlantedDate(log) &&
    daysBetweenIsoDates(log.plantedDate, today) >= 0;
}

export function isFuturePlantedLog(log: PlantingLog, today: string): boolean {
  return log.status !== 'finished' &&
    log.status !== 'dormant' &&
    hasValidPlantedDate(log) &&
    daysBetweenIsoDates(log.plantedDate, today) < 0;
}

export function shouldGenerateCareTasks(log: PlantingLog, today: string): boolean {
  return isActivePlantedLog(log, today);
}

export function isPlannedOnly(value: PlantingLog | string | undefined | null, plannedPlants: string[] = []): boolean {
  if (!value) return false;
  if (typeof value === 'string') return plannedPlants.includes(value);
  if (hasValidPlantedDate(value)) return false;
  return plannedPlants.includes(value.plantId);
}

export function taskEffectiveDueDate(task: GuidanceTaskLike): string {
  return task.dueDateOverride ?? task.dueDate;
}

export function isStaleHarvestTask(task: GuidanceTaskLike, today: string): boolean {
  if (task.category !== 'harvesting') return false;
  return daysBetweenIsoDates(taskEffectiveDueDate(task), today) > 14;
}

export function buildGuidanceContext({
  logs = [],
  plannedPlants = [],
  weather = null,
  today,
  zone = null,
  plantLookup = new Map<string, ResolvedPlant>(),
  taskState = null,
}: GuidanceContextInput): GuidanceContext {
  const anchorDate = guidanceAnchorDate(weather, today);
  const activePlantedLogs = logs.filter(log => isActivePlantedLog(log, anchorDate));
  const futurePlantedLogs = logs.filter(log => isFuturePlantedLog(log, anchorDate));
  const resolvedPlants = new Map<string, ResolvedPlant>();

  for (const log of logs) {
    const plant = plantLookup.get(log.plantId);
    if (plant) resolvedPlants.set(log.plantId, plant);
  }

  return {
    today: anchorDate,
    anchorDate,
    zone,
    weather,
    plantLookup,
    resolvedPlants,
    allPlantingLogs: logs,
    activePlantedLogs,
    futurePlantedLogs,
    plannedOnlyPlantIds: plannedPlants,
    taskState,
  };
}
